import React from 'react';
import {
  IconButton,
  InputAdornment,
  Container,
  CssBaseline,
  TextField,
  Button,
  Typography,
  makeStyles,
  Snackbar,
} from '@material-ui/core';
import {Visibility, VisibilityOff} from '@material-ui/icons';
import {useLocation, useHistory} from 'react-router-dom';
import BaliardaLogo from '../../content/images/logos baliarda/logo-color.png';
import {Trans} from '@lingui/macro';
import {i18n2} from 'features/i18n/i18n';
import {
  hasNumber,
  hasSymbol,
  hasObviousSequence,
} from 'features/collaborator/model/PasswordValidations';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    height: 'auto',
    width: 'auto',
    maxHeight: '200px',
    verticalAlign: 'middle',
  },
  form: {width: '100%', marginTop: theme.spacing(1)},
  submit: {margin: theme.spacing(3, 0, 2)},
}));

export default function ResetPassword() {
  const classes = useStyles();
  const query = useQuery();
  const history = useHistory();
  const token = query.get('token') || '';
  const [newPass, setNewPass] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [settings, setSettings] = React.useState(null);
  const [message, setMessage] = React.useState({open: false, text: ''});
  const [loading, setLoading] = React.useState(false);
  const [showNewPass, setShowNewPass] = React.useState(false);
  const [showConfirmPass, setShowConfirmPass] = React.useState(false);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  React.useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/Settings');
        if (r.ok) {
          const s = await r.json();
          setSettings(s);
        }
      } catch (e) {
        // ignore and validate on server
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPass !== confirm) {
      setMessage({open: true, text: 'Las contraseñas no coinciden'});
      return;
    }

    // Client-side validation using settings (best-effort; server enforces full policy)
    if (settings) {
      if (
        settings.pwdRequireMinLength &&
        (newPass?.length ?? 0) < settings.pwdMinLength
      ) {
        setMessage({
          open: true,
          text: `La contraseña debe tener al menos ${settings.pwdMinLength} caracteres`,
        });
        return;
      }
      if (settings.pwdRequireNumber && !hasNumber(newPass)) {
        setMessage({
          open: true,
          text: 'La contraseña debe contener al menos un número',
        });
        return;
      }
      if (settings.pwdRequireSymbol && !hasSymbol(newPass)) {
        setMessage({
          open: true,
          text: 'La contraseña debe contener al menos un símbolo',
        });
        return;
      }
      if (settings.pwdDisallowObviousSequences && hasObviousSequence(newPass)) {
        setMessage({
          open: true,
          text: 'La contraseña contiene secuencias obvias',
        });
        return;
      }
      // pwdDisallowUserData cannot be validated client-side reliably here (no user data)
    }
    setLoading(true);
    try {
      const res = await fetch('/api/Auth/reset-password', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({Token: token, NewPassword: newPass}),
      });
      if (res.ok) {
        setMessage({open: true, text: 'Contraseña restablecida correctamente'});
        setTimeout(() => history.push('/login'), 1500);
      } else {
        const txt = await res.text();
        setMessage({open: true, text: txt || 'Token inválido o expirado'});
      }
    } catch (err) {
      setMessage({open: true, text: 'Error de conexión'});
    }
    setLoading(false);
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <img src={BaliardaLogo} className={classes.logo} alt="Mox It Logo" />
        <Typography component="h1" variant="h5">
          <Trans>Restablecer contraseña</Trans>
        </Typography>
        <form className={classes.form} onSubmit={handleSubmit}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={i18n2._('Nueva contraseña')}
            name="newPassword"
            type={showNewPass ? 'text' : 'password'}
            onChange={(e) => setNewPass(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showNewPass
                        ? i18n2._('Ocultar contraseña')
                        : i18n2._('Mostrar contraseña')
                    }
                    onClick={() => setShowNewPass(!showNewPass)}
                    onMouseDown={handleMouseDownPassword}
                    edge="end">
                    {showNewPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={i18n2._('Confirmar contraseña')}
            name="confirmPassword"
            type={showConfirmPass ? 'text' : 'password'}
            onChange={(e) => setConfirm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showConfirmPass
                        ? i18n2._('Ocultar contraseña')
                        : i18n2._('Mostrar contraseña')
                    }
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    onMouseDown={handleMouseDownPassword}
                    edge="end">
                    {showConfirmPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={loading}>
            <Trans>Restablecer</Trans>
          </Button>
        </form>
      </div>
      <Snackbar
        open={message.open}
        autoHideDuration={4000}
        onClose={() => setMessage({...message, open: false})}
        message={i18n2._(message.text)}
      />
    </Container>
  );
}
